import { useQuery } from '@tanstack/react-query';
import { lambdaDataService } from '@/services/LambdaDataService';

// Fallback countries list for when Lambda API is unavailable
const FALLBACK_COUNTRIES = [
  { code: 'US', name: 'United States', currency: 'USD' },
  { code: 'IN', name: 'India', currency: 'INR' },
  { code: 'NP', name: 'Nepal', currency: 'NPR' },
  { code: 'GB', name: 'United Kingdom', currency: 'GBP' },
  { code: 'CA', name: 'Canada', currency: 'CAD' },
  { code: 'AU', name: 'Australia', currency: 'AUD' },
  { code: 'DE', name: 'Germany', currency: 'EUR' },
  { code: 'FR', name: 'France', currency: 'EUR' },
  { code: 'JP', name: 'Japan', currency: 'JPY' },
  { code: 'CN', name: 'China', currency: 'CNY' },
  { code: 'SG', name: 'Singapore', currency: 'SGD' },
  { code: 'MY', name: 'Malaysia', currency: 'MYR' },
  { code: 'TH', name: 'Thailand', currency: 'THB' },
  { code: 'ID', name: 'Indonesia', currency: 'IDR' },
  { code: 'PH', name: 'Philippines', currency: 'PHP' },
  { code: 'VN', name: 'Vietnam', currency: 'VND' },
  { code: 'BD', name: 'Bangladesh', currency: 'BDT' },
  { code: 'LK', name: 'Sri Lanka', currency: 'LKR' },
  { code: 'PK', name: 'Pakistan', currency: 'PKR' },
  { code: 'AE', name: 'United Arab Emirates', currency: 'AED' },
];

export const useAllCountries = () => {
  const result = useQuery({
    queryKey: ['country_settings_table', 'all_countries_array'],
    staleTime: 60 * 60 * 1000, // 🚀 PERFORMANCE: 1 hour cache - countries don't change often
    gcTime: 24 * 60 * 60 * 1000, // 🚀 PERFORMANCE: 24 hours garbage collection
    refetchOnMount: false, // 🔧 FIX: Prevent tab-switch refetches
    refetchOnWindowFocus: false, // 🔧 FIX: Prevent focus-based refetches
    queryFn: async () => {
      // 🔧 FIX: Add development debugging for loading state transitions
      if (import.meta.env.DEV) {
        console.log('[useAllCountries] Fetching countries data...');
      }

      try {
        const response = await lambdaDataService.getCountries();

        if (!response.success) {
          throw new Error('Failed to fetch countries');
        }

        // Ensure we always return an array
        const result = Array.isArray(response.data) ? response.data : [];

        if (import.meta.env.DEV) {
          console.log(`[useAllCountries] Loaded ${result.length} countries from Lambda`);
        }

        return result;
      } catch (error) {
        // Fallback to static countries list when Lambda API fails
        console.warn('[useAllCountries] Lambda API failed, using fallback countries:', error);

        if (import.meta.env.DEV) {
          console.log(`[useAllCountries] Using fallback: ${FALLBACK_COUNTRIES.length} countries`);
        }

        return FALLBACK_COUNTRIES;
      }
    },
  });
  
  // 🔧 FIX: Add development debugging for cache states
  if (import.meta.env.DEV && result.isLoading) {
    console.log('[useAllCountries] Loading state:', {
      isLoading: result.isLoading,
      isFetching: result.isFetching,
      isStale: result.isStale,
      dataUpdatedAt: result.dataUpdatedAt ? new Date(result.dataUpdatedAt).toISOString() : null
    });
  }
  
  return result;
};
